import { Badge, Box, Button, type ButtonProps, Flex, Icon, Popover, Spacer, Steps, Text, useBreakpointValue } from '@chakra-ui/react';
import { BrandLogo } from './BrandLogo';
import { FiArrowLeft, FiList } from 'react-icons/fi';
import { getEnrollmentStepValidation, useEnrollment } from '../pages/enrollment/EnrollmentContext';
import { useFirebaseAuth } from '../hooks/useFirebaseAuth';
import { useNavigate } from 'react-router-dom';
import { UserMenu } from './UserMenu';
import { useSettings } from '../hooks/useSettings';
import { useSignOut } from 'react-firebase-hooks/auth';
import { useUser } from '../hooks/useUser';

const steps = [
    { title: 'Terminy', description: 'Wybór zajęć' },
    { title: 'Kontakt', description: 'Twoje dane' },
    { title: 'Podsumowanie', description: 'Podsumowanie' },
];

type GuidedPopoverProps = {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactElement;
    message: string;
};

function GuidedPopover({ isOpen, onClose, children, message }: GuidedPopoverProps) {
    return (
        <Popover.Root
            open={isOpen}
            onOpenChange={(details) => {
                if (!details.open) onClose();
            }}
            closeOnInteractOutside
            closeOnEscape={false}
            positioning={{ placement: 'bottom-end', gutter: 12 }}
        >
            {isOpen && (
                <Box
                    position="fixed"
                    inset={0}
                    bg="blackAlpha.600"
                    zIndex={1}
                    aria-hidden="true"
                />
            )}
            <Popover.Trigger asChild>
                <Box position="relative" zIndex={isOpen ? 2 : undefined}>
                    {children}
                </Box>
            </Popover.Trigger>
            {isOpen && (
                <Popover.Positioner zIndex={3}>
                    <Popover.Content maxW="xs" p={4} shadow="lg">
                        <Popover.Arrow>
                            <Popover.ArrowTip />
                        </Popover.Arrow>
                        <Popover.Description>{message}</Popover.Description>
                        <Button mt={4} w="100%" onClick={onClose}>
                            OK
                        </Button>
                    </Popover.Content>
                </Popover.Positioner>
            )}
        </Popover.Root>
    );
}

function GuidedNextButton({ children, onClick, ...props }: ButtonProps) {
    const enrollment = useEnrollment();
    const isGuideOpen = enrollment.enrollmentGuide === 'next-step';

    const handleClick: ButtonProps['onClick'] = (event) => {
        if (isGuideOpen) enrollment.showSelectedLessonsGuide();
        onClick?.(event);
    };

    return (
        <GuidedPopover
            isOpen={isGuideOpen}
            onClose={enrollment.showSelectedLessonsGuide}
            message="Po wybraniu interesujących Cię terminów, przejdź dalej, aby wysłać je do nas do zatwierdzenia."
        >
            <Button {...props} onClick={handleClick}>
                {children}
            </Button>
        </GuidedPopover>
    );
}

export function EnrollmentHeader() {
    const enrollment = useEnrollment();
    const navigate = useNavigate();
    const auth = useFirebaseAuth();
    const [signOut] = useSignOut(auth);
    const [user, userLoading] = useUser();
    const [settings] = useSettings();
    const isMobile = useBreakpointValue({ base: true, lg: false }) ?? false;
    const enrollmentAvailable = settings?.enrollmentAvailable ?? true;

    const handleLogout = async () => {
        try {
            await signOut();
        } catch (err) {
            console.error(err);
        }
        navigate('/');
    };

    const stepValidation = getEnrollmentStepValidation({
        step: enrollment.step,
        lessonsList: enrollment.lessonsList,
        user,
        userLoading,
        statuteAccepted: enrollment.statuteAccepted,
    });

    const canGoNext = stepValidation.isComplete;
    const currentStep = steps[enrollment.step] ?? steps[steps.length - 1];

    if (!enrollmentAvailable) {
        return (
            <Flex px={4} alignItems="center" justify="space-between" gap={3} py={2}>
                <BrandLogo compact={isMobile} />
                <Button
                    size={isMobile ? 'sm' : 'md'}
                    variant="ghost"
                    colorPalette="orange"
                    onClick={() => navigate(isMobile ? '/' : '/?noRedirectToPanel')}
                >
                    <Icon as={FiArrowLeft} boxSize={4} mr={isMobile ? 0 : 2} />
                    {!isMobile && 'Powrót'}
                </Button>
            </Flex>
        );
    }

    const renderEnrollmentProgress = () => (
        <Flex align="center" justify="center" gap={4} px={4} py={2} w="100%">
            {!isMobile && (
                <Button
                    size="sm"
                    variant="plain"
                    colorPalette="orange"
                    onClick={enrollment.goToPrevStep}
                    disabled={enrollment.step === 0}
                >
                    Wstecz
                </Button>
            )}

            <Steps.Root
                step={enrollment.step}
                size="sm"
                maxW="60%"
                textAlign="center"
                colorPalette="orange"
                orientation="horizontal"
                mx="auto"
            >
                <Steps.List>
                    {steps.map((step, index) => (
                        <Steps.Item key={index} index={index}>
                            <Steps.Indicator>
                                <Steps.Status
                                    complete={<Box>✓</Box>}
                                    incomplete={<Box>{index + 1}</Box>}
                                    current={<Box>{index + 1}</Box>}
                                />
                            </Steps.Indicator>

                            <Box flexShrink="0">
                                <Steps.Description fontSize="sm" color="brand.orange.500" fontWeight={enrollment.step === index ? "semibold" : "thin"} textTransform="uppercase" letterSpacing="wider">
                                    {step.description}
                                </Steps.Description>
                            </Box>

                            <Steps.Separator />
                        </Steps.Item>
                    ))}
                </Steps.List>
            </Steps.Root>

            {!isMobile && (
                enrollment.step < 2 ?
                <GuidedNextButton
                    size="sm"
                    colorPalette="orange"
                    onClick={() => {
                        if (canGoNext) enrollment.goToNextStep();
                    }}
                    disabled={!canGoNext || enrollment.step >= 2}
                >
                    Dalej
                </GuidedNextButton>
                : <Box w="60px" />
            )}
        </Flex>
    );

    const renderMobileControls = () => {
        if (enrollment.step >= steps.length) return null;

        const handlePrimaryAction = () => {
            if (enrollment.step === steps.length - 1) return;
            if (canGoNext) enrollment.goToNextStep();
        };

        const canGoBack = enrollment.step > 0 && enrollment.step < steps.length;

        return (
            <Flex align="center" justify="space-between" w="100%" gap={2} px={4} py={2}>
                <Box w="40px" flexShrink={0}>
                    {canGoBack && (
                        <Button
                            onClick={enrollment.goToPrevStep}
                            variant="outline"
                            size="sm"
                            aria-label="Poprzedni krok"
                        >
                            <Icon as={FiArrowLeft} />
                        </Button>
                    )}
                </Box>

                <Box flex="1" textAlign="center" >
                    <Text fontSize='sm' color="brand.orange.500" fontWeight="thin" textTransform="uppercase" letterSpacing="wider">
                        {currentStep?.description || 'Krok'}
                    </Text>
                </Box>

                <GuidedNextButton
                    onClick={handlePrimaryAction}
                    colorPalette="orange"
                    variant="solid"
                    size="sm"
                    aria-label={enrollment.step === steps.length - 1 ? 'Rezerwuj' : 'Następny krok'}
                    disabled={!canGoNext && enrollment.step !== steps.length - 1}
                >
                    {enrollment.step === steps.length - 1 ? 'Rezerwuj' : 'Dalej'}
                </GuidedNextButton>
            </Flex>
        );
    };

    return (
        <>
            <Flex px={4} alignItems="center" gap={3}>
                <BrandLogo compact={isMobile} />
                <Spacer />

                <GuidedPopover
                    isOpen={enrollment.enrollmentGuide === 'selected-lessons'}
                    onClose={enrollment.hideEnrollmentGuide}
                    message="Aby przeglądać i edytować wybrane terminy kliknij tutaj."
                >
                    <Button
                        colorPalette="orange"
                        variant={enrollment.lessonsList.length ? 'subtle' : 'ghost'}
                        onClick={enrollment.toggleBasket}
                        size={isMobile ? 'sm' : 'md'}
                        justifyContent="flex-start"
                        minW={0}
                        aria-label="Lista wybranych zajęć"
                        px={isMobile ? 2 : 3}
                    >
                        <Icon as={FiList} />
                        <Box as="span" ml={2} whiteSpace="nowrap">
                            Wybrane terminy
                        </Box>
                        <Badge
                            colorPalette="orange"
                            ml={isMobile ? 0 : 2}
                            minW={isMobile ? '1.5rem' : 'auto'}
                            h={isMobile ? '1.5rem' : 'auto'}
                            borderRadius="full"
                            px={isMobile ? 1 : 2}
                            py={isMobile ? 0 : 1}
                            fontSize={isMobile ? 'xs' : 'sm'}
                            display="inline-flex"
                            alignItems="center"
                            justifyContent="center"
                        >
                            {enrollment.lessonsList.length}
                        </Badge>
                    </Button>
                </GuidedPopover>

                {user && <UserMenu user={user} handleLogout={handleLogout} />}
            </Flex>

            {isMobile ? renderMobileControls() : renderEnrollmentProgress()}
        </>
    );
}
